import React, { useState, useEffect } from 'react';

/**
 * HelpSection Component
 * 
 * Sticky support section positioned at bottom-left of dashboard.
 * Provides quick access to customer support via email.
 * 
 * Features:
 * - Sticky positioning (bottom-left, 20px from edges)
 * - Slide-in animation from left on mount
 * - Hover lift effect with glow border
 * - Hidden on small mobile (<640px)
 * - Email link: support@letstalkstrategy.io
 * 
 * Design:
 * - Card-based design with shadow
 * - Consistent with dashboard aesthetic
 * - Responsive and accessible
 */

const HelpSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  // Trigger slide-in animation after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 400); // 400ms delay for staggered entrance

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`
        fixed bottom-6 left-6 z-40
        hidden sm:block
        transition-all duration-500 ease-out
        ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}
      `}
    >
      <div
        className="
          bg-white rounded-xl shadow-lg border border-gray-200
          p-5 w-72
          hover:shadow-xl hover:-translate-y-1 hover:border-blue-300
          transition-all duration-300
          group
        "
      >
        {/* Icon & Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="
            w-10 h-10 rounded-full 
            bg-gradient-to-br from-blue-500 to-teal-500
            flex items-center justify-center
            group-hover:scale-110 transition-transform duration-300
          ">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Need Help?</h3>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          Have questions about your tax strategy? Our team is here to help you maximize your savings.
        </p>

        {/* Contact Button */}
        <a
          href="mailto:support@letstalkstrategy.io?subject=Support Request from Dashboard"
          className="
            block w-full
            bg-gradient-to-r from-blue-600 to-teal-600
            hover:from-blue-700 hover:to-teal-700
            text-white font-medium
            px-4 py-3 rounded-lg
            text-center text-sm
            transition-all duration-300
            shadow-md hover:shadow-lg
            flex items-center justify-center gap-2
          "
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          Contact Support
        </a>

        {/* Email display */}
        <p className="text-xs text-gray-500 text-center mt-2">
          support@letstalkstrategy.io
        </p>
      </div>
    </div>
  );
};

export default HelpSection;
